<template>
    <a-tabs
        default-active-key="1"
        tab-position="top"
        type="card"
        :tabBarStyle="tabBarStyle"
    >
        <a-tab-pane key="1" tab="Vistoria">
            <div style="display:flex; flex-direction:column; gap:10px; margin-top:10px;">
                <span style="font-weight:bold;">Fotos lote</span>
                <FileCarousel :files="subdivisionImages" />
            </div>

            <div style="display:flex; flex-direction:column; gap:10px; margin-top:20px;">
                <span style="font-weight:bold;">Fotos edificações</span>
                <FileCarousel :files="buildingImages" />
            </div>
        </a-tab-pane>

        <a-tab-pane key="2" tab="Fotos">
            <a-button type="dashed" @click="showUploadModal = true" style="margin-bottom: 16px" title="Anexar">
                <PlusOutlined />
            </a-button>

            <a-modal
                v-model:open="showUploadModal"
                title="Upload de Imagem"
                :destroy-on-close="true"
                :footer="null"
            >
                <UploadImage :property-id="props.propertyId" @on-upload="handleUploadSuccess" />
            </a-modal>

            <DocumentFiles
                :files="photoFiles"
                :separate-sources="enableEstorage"
                :estorage-misconfigured="estorageMisconfigured"
            />
        </a-tab-pane>

        <a-tab-pane key="3" tab="Planta">
            <UploadFile :property-id="props.propertyId" :type="2" @on-upload="fetchData"></UploadFile>
            <DocumentFiles
                :files="plantFiles"
                :separate-sources="enableEstorage"
                :estorage-misconfigured="estorageMisconfigured"
            />
        </a-tab-pane>

        <a-tab-pane key="4" tab="Croqui">
            <UploadFile :property-id="props.propertyId" :type="3" @on-upload="fetchData"></UploadFile>
            <DocumentFiles
                :files="sketchFiles"
                :separate-sources="enableEstorage"
                :estorage-misconfigured="estorageMisconfigured"
            />
        </a-tab-pane>

        <a-tab-pane key="5" tab="Compra/Venda">
            <UploadFile :property-id="props.propertyId" :type="4" @on-upload="fetchData"></UploadFile>
            <DocumentFiles
                :files="buySellFiles"
                :separate-sources="enableEstorage"
                :estorage-misconfigured="estorageMisconfigured"
            />
        </a-tab-pane>
    </a-tabs>
</template>

<script setup>
import { ref, computed, defineProps, onMounted } from 'vue'
import { PlusOutlined } from '@ant-design/icons-vue'
import FileCarousel from '../../../FileCarousel.vue'
import UploadImage from './UploadImage.vue'
import UploadFile from './UploadFile.vue'
import DocumentFiles from './DocumentFiles.vue'
import { openNotification } from '../../../../../../utils/utilities.js'

const enableEstorage = Boolean(window.enableEstorage)

const props = defineProps({
    propertyId: Number,
})

const idProperty = ref(props.propertyId)

const data = ref([])
const subdivisionImages = ref([])
const buildingImages = ref([])
const estorageMisconfigured = ref(false)

const tabBarStyle = {
    fontSize: '10px',
    padding: '0 2px',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-start'
}

const filesByType = (type) => computed(() => data.value.filter(f => f.type === type))

const photoFiles = filesByType(1)
const plantFiles = filesByType(2)
const sketchFiles = filesByType(3)
const buySellFiles = filesByType(4)

const checkEstorageAccess = async (attachments) => {
    estorageMisconfigured.value = false

    if (!enableEstorage) {
        return
    }

    const estorageFile = attachments.find(file => file.estorageId)
    if (!estorageFile) {
        return
    }

    try {
        const response = await fetch(`/proxy/estorage?id=${estorageFile.estorageId}`, {
            method: 'GET',
            credentials: 'same-origin',
        })

        if (response.status === 401) {
            estorageMisconfigured.value = true
        }
    } catch (error) {
        estorageMisconfigured.value = true
    }
}

const loadLastCompletedInspectionImages = async () => {
    const inspections = (await window.axios.get(`/api/inspections/${idProperty.value}/getAllInspections`)).data.data
    const lastCompletedInspection = inspections.find(inspection => inspection.status === 'Completado')

    if (!lastCompletedInspection) {
        return
    }

    const inspectionImages = (await window.axios.get(`/api/inspections/images/${lastCompletedInspection.id}`)).data.data

    subdivisionImages.value = inspectionImages.subdivisionImages.map(url => ({
        typeFile: 'image',
        url,
    }))

    buildingImages.value = inspectionImages.buildingsImages.flat().map(url => ({
        typeFile: 'image',
        url,
    }))
}

const fetchData = async () => {
    try {
        const attachments = (await window.axios.get(`/api/map-actions/bic/property-data/${idProperty.value}/attachments`)).data.data
        await loadLastCompletedInspectionImages()

        data.value = attachments.filter(file => file.type !== 0)
        await checkEstorageAccess(data.value)
    } catch (error) {
        openNotification('error', error.response?.data?.message || 'Não foi possível carregar os arquivos.')
    }
}

const showUploadModal = ref(false)

const handleUploadSuccess = async () => {
    showUploadModal.value = false
    await fetchData()
}

onMounted(async () => {
    await fetchData()
})
</script>
